import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Tabs, Tab, Dialog, DialogContent, DialogActions,
  Button, Typography, TextField, MenuItem, Select,
  InputLabel, FormControl, Stack, Checkbox, FormControlLabel,
  InputAdornment, Chip, Divider, Paper, IconButton, Tooltip, Autocomplete
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FolderIcon from '@mui/icons-material/Folder';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import CollapsibleTable from '../../components/table';
import ModalCentroCosto from '../../components/ModalCentroCosto';
import DoubleConfirmModal from '../../components/DoubleConfirmModal';
import BotonAgregar from '../../components/BotonAgregar';
import ExportButton from '../../components/BotonEx';
import { useSession } from '../../components/hooks/useSession';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';
import config from '../../config/config';
import axios from 'axios';

const ACCENT = '#0655A5';

const filterFieldSx = {
  width: 'clamp(145px, 14vw, 200px)',
  '& .MuiInputBase-root': {
    height: 'clamp(28px, 2.2vw, 32px)',
    minHeight: 'clamp(28px, 2.2vw, 32px)',
    maxHeight: 'clamp(28px, 2.2vw, 32px)',
    flexWrap: 'nowrap',
    borderRadius: '7px',
    backgroundColor: '#ffffff',
    paddingRight: '32px !important',
    '& fieldset': { borderColor: '#cbd5e1' },
    '&:hover fieldset': { borderColor: '#94a3b8' },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
  },
  '& .MuiInputBase-input': {
    py: 0.2,
    fontSize: 'clamp(0.66rem, 0.62rem + 0.08vw, 0.74rem)',
    minWidth: '24px !important',
  },
  '& .MuiInputLabel-root': {
    fontSize: 'clamp(0.66rem, 0.62rem + 0.06vw, 0.74rem)',
    color: '#64748b',
    transform: 'translate(10px, 7px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(12px, -7px) scale(0.75)',
      backgroundColor: '#ffffff',
      px: 0.5,
      fontWeight: 700,
      color: ACCENT
    }
  },
  '& .MuiAutocomplete-endAdornment': {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    right: '4px',
    display: 'flex',
    alignItems: 'center',
    '& .MuiIconButton-root': {
      padding: '2px',
      margin: 0,
      '& svg': { fontSize: '15px', color: '#64748b' }
    }
  }
};

const renderSummaryInput = (params, label, selected = []) => {
  let summary = '';
  if (selected.length === 1) summary = selected[0];
  else if (selected.length > 1) summary = `${selected.length} seleccionados`;

  return (
    <TextField
      {...params}
      label={label}
      InputProps={{
        ...params.InputProps,
        startAdornment: summary ? (
          <Box
            component="span"
            sx={{
              maxWidth: 'calc(100% - 36px)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 'clamp(0.66rem, 0.62rem + 0.08vw, 0.74rem)',
              fontWeight: 600,
              color: '#0f172a',
              pl: 0.2,
            }}
          >
            {summary}
          </Box>
        ) : params.InputProps.startAdornment,
      }}
    />
  );
};

const elegantInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    fontSize: 'clamp(0.72rem, 0.68rem + 0.25vw, 0.90rem)',
    height: 'clamp(30px, 2.5vw, 42px)',
    transition: 'box-shadow .15s, border-color .15s',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
    '&.Mui-focused': { boxShadow: '0 0 0 2px rgba(6,85,165,.12)', backgroundColor: '#ffffff' },
  },
  '& .MuiInputLabel-root': {
    fontSize: 'clamp(0.70rem, 0.66rem + 0.2vw, 0.86rem)',
    fontWeight: 600,
    transform: 'translate(12px, 8px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -8px) scale(0.75)',
      backgroundColor: '#ffffff',
      px: 0.5
    }
  },
  '& .MuiFormHelperText-root': { ml: 0, fontSize: 'clamp(0.66rem, 0.62rem + 0.1vw, 0.76rem)', mt: 0.3 },
};

// Helpers sesión
const getSessionString = (key) => sessionStorage.getItem(key) || '';

const getSessionArray = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    if (!value) return [];
    const s = String(value).trim();
    if (s.startsWith('[') && s.endsWith(']')) {
      const parsed = JSON.parse(s);
      return Array.isArray(parsed) ? parsed : [];
    }
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  } catch (e) {
    console.error('Error parseando', key, e);
    return [];
  }
};

const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];
  if (Array.isArray(idEmpresa)) {
    return idEmpresa.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();
    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) {
          return arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
        }
      } catch {}
    }
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }
  if (typeof idEmpresa === 'number' && Number.isFinite(idEmpresa) && idEmpresa > 0) {
    return [idEmpresa];
  }
  return [];
};

const sortInactivosAlFinal = (list) => {
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => {
    const actA = Boolean(a.ACTIVO);
    const actB = Boolean(b.ACTIVO);
    if (actA === actB) return 0;
    return actA ? -1 : 1;
  });
};

export default function CentroCostos() {
  const apiUrl = config.apiBaseUrl;
  useSession();

  const [currentTab, setCurrentTab] = useState(2);

  // ─── Datos de sesión reactivos ────────────────────────────────────────
  const [loggedInUsername, setLoggedInUsername] = useState(getSessionString('loggedInUsername'));
  const [idEmpresa, setIdEmpresa] = useState(getSessionArray('EmpresaId'));

  const usuarioId = useMemo(() => Number(sessionStorage.getItem('UsuarioId') || 0), []);

  const empresaIdsKey = useMemo(() => {
    const arr = normalizeEmpresaArray(idEmpresa);
    return arr.length ? arr.join(',') : '';
  }, [idEmpresa]);

  const empresaIds = useMemo(() => {
    if (!empresaIdsKey) return [];
    return empresaIdsKey
      .split(',')
      .map((n) => Number(n))
      .filter((x) => Number.isFinite(x) && x > 0);
  }, [empresaIdsKey]);

  const empresaIdPref = useMemo(() => {
    return empresaIds.length > 1 ? 0 : (empresaIds[0] ?? 0);
  }, [empresaIds]);

  useEffect(() => {
    const handleEmpresaChange = () => {
      const nuevosIds = getSessionArray('EmpresaId');
      const nuevoUser = getSessionString('loggedInUsername');
      setIdEmpresa(nuevosIds);
      setLoggedInUsername(nuevoUser);
    };

    window.addEventListener('empresaCambiada', handleEmpresaChange);
    return () => window.removeEventListener('empresaCambiada', handleEmpresaChange);
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // 1. PESTAÑA CENTROS DE COSTO
  // ───────────────────────────────────────────────────────────────────────
  const [data, setData] = useState([]);
  const [empresasDisponibles, setEmpresasDisponibles] = useState([]);
  const [formData, setFormData] = useState({
    CENTRO_COSTO: '',
    NUMERO_CC: '',
    PROYECTO: '',
    DIVISION: '',
    IDEMPRESA: ''
  });
  const [selectedCC, setSelectedCC] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // ─── Filtros de la barra ───
  const [filtroEmpresas, setFiltroEmpresas] = useState([]);
  const [filtroProyectos, setFiltroProyectos] = useState([]);
  const [filtroGrupos, setFiltroGrupos] = useState([]);
  const [filtroBuscar, setFiltroBuscar] = useState('');

  const [appliedEmpresas, setAppliedEmpresas] = useState([]);
  const [appliedProyectos, setAppliedProyectos] = useState([]);
  const [appliedGrupos, setAppliedGrupos] = useState([]);
  const [appliedBuscar, setAppliedBuscar] = useState('');

  const handleBuscar = () => {
    setAppliedEmpresas(filtroEmpresas);
    setAppliedProyectos(filtroProyectos);
    setAppliedGrupos(filtroGrupos);
    setAppliedBuscar(filtroBuscar);
  };

  const handleLimpiar = () => {
    setFiltroEmpresas([]);
    setFiltroProyectos([]);
    setFiltroGrupos([]);
    setFiltroBuscar('');

    setAppliedEmpresas([]);
    setAppliedProyectos([]);
    setAppliedGrupos([]);
    setAppliedBuscar('');
  };

  const fetchEmpresas = useCallback(() => {
    if (!apiUrl) return;
    axios.post(`${apiUrl}/empresasDisponibles`, { idEmpresa: empresaIds })
      .then((res) => setEmpresasDisponibles(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [apiUrl, empresaIds]);

  const fetchData = useCallback(() => {
    if (!apiUrl || !empresaIdsKey) return;
    fetchEmpresas();
    axios
      .post(`${apiUrl}/centroCostosTabla`, { idEmpresa: empresaIds })
      .then((res) => {
        if (res.data.mensaje) {
          handleOpenSweetAlert(res.data.mensaje, 'error');
          setData([]);
        } else {
          const list = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
          setData(sortInactivosAlFinal(list));
        }
      })
      .catch((err) => {
        handleOpenSweetAlert(err.response?.data?.error || err.response?.data?.mensaje || 'Error al procesar', 'error');
      });
  }, [apiUrl, empresaIds, empresaIdsKey, fetchEmpresas]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const abrirModal = useCallback((row = null) => {
    if (row) {
      setSelectedCC(row);
      setFormData({
        CENTRO_COSTO: row.CENTRO_COSTO || row.NOMBRE || '',
        NUMERO_CC: row.NUMERO_CC || row.NUMERO || '',
        ID_PROYECTO: row.ID_PROYECTO || null,
        PROYECTO: row.PROYECTO || row.NOMBRE_PROYECTO || '',
        ID_DIVISION: row.ID_DIVISION || null,
        DIVISION: row.DIVISION || row.GRUPO_CC || row.GRUPO || '',
        IDEMPRESA: row.IDEMPRESA ? Number(row.IDEMPRESA) : (empresaIds.length === 1 ? empresaIds[0] : ''),
      });
    } else {
      setSelectedCC(null);
      setFormData({
        CENTRO_COSTO: '',
        NUMERO_CC: '',
        ID_PROYECTO: null,
        PROYECTO: '',
        ID_DIVISION: null,
        DIVISION: '',
        IDEMPRESA: empresaIds.length === 1 ? empresaIds[0] : '',
      });
    }
    setModalOpen(true);
  }, [empresaIds]);

  const cerrarModal = () => {
    setModalOpen(false);
    setSelectedCC(null);
    setFormData({ CENTRO_COSTO: '', NUMERO_CC: '', ID_PROYECTO: null, PROYECTO: '', ID_DIVISION: null, DIVISION: '', IDEMPRESA: '' });
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ───────────────────────────────────────────────────────────────────────
  // 2. PESTAÑA PROYECTOS (NIVEL 1)
  // ───────────────────────────────────────────────────────────────────────
  const [proyectosData, setProyectosData] = useState([]);
  const [modalProyOpen, setModalProyOpen] = useState(false);
  const [formProy, setFormProy] = useState({ ID_PROYECTO: null, NOMBRE_PROYECTO: '', IDEMPRESA: '' });
  const [errorsProy, setErrorsProy] = useState({});

  const fetchProyectos = useCallback(() => {
    if (!apiUrl) return;
    axios.post(`${apiUrl}/proyectosTabla`, { idEmpresa: empresaIds })
      .then((res) => {
        const raw = res.data?.contenido || [];
        const mapped = raw.map(p => ({
          NOMBRE_PROYECTO: p.NOMBRE_PROYECTO || '',
          NOMBRE_EMPRESA: p.NOMBRE_EMPRESA || 'Sin Asignar',
          ACTIVO: p.ACTIVO,
          ID_PROYECTO: p.ID_PROYECTO,
          IDEMPRESA: p.IDEMPRESA
        }));
        setProyectosData(sortInactivosAlFinal(mapped));
      })
      .catch((err) => console.error('Error cargando proyectos:', err));
  }, [apiUrl, empresaIds, empresaIdsKey]);

  // ───────────────────────────────────────────────────────────────────────
  // 3. PESTAÑA DIVISIONES (NIVEL 2)
  // ───────────────────────────────────────────────────────────────────────
  const [divisionesData, setDivisionesData] = useState([]);
  const [modalDivOpen, setModalDivOpen] = useState(false);
  const [formDiv, setFormDiv] = useState({ ID_DIVISION: null, NOMBRE_DIVISION: '', ID_PROYECTO: '', IDEMPRESA: '' });
  const [errorsDiv, setErrorsDiv] = useState({});

  const [modalAsignarOpen, setModalAsignarOpen] = useState(false);
  const [selectedDivisionForAssign, setSelectedDivisionForAssign] = useState(null);
  const [selectedCcids, setSelectedCcids] = useState([]);
  const [searchCcText, setSearchCcText] = useState('');
  const [loadingAssign, setLoadingAssign] = useState(false);

  const fetchDivisiones = useCallback(() => {
    if (!apiUrl) return;
    axios.post(`${apiUrl}/divisionesTabla`, { idEmpresa: empresaIds })
      .then((res) => {
        const raw = res.data?.contenido || [];
        const mapped = raw.map(d => ({
          NOMBRE_DIVISION: d.NOMBRE_DIVISION || '',
          PROYECTO: d.PROYECTO || '',
          NOMBRE_EMPRESA: d.NOMBRE_EMPRESA || 'Sin Asignar',
          ACTIVO: d.ACTIVO,
          ID_DIVISION: d.ID_DIVISION,
          ID_PROYECTO: d.ID_PROYECTO,
          IDEMPRESA: d.IDEMPRESA
        }));
        setDivisionesData(sortInactivosAlFinal(mapped));
      })
      .catch((err) => console.error('Error cargando divisiones:', err));
  }, [apiUrl, empresaIds, empresaIdsKey]);

  useEffect(() => {
    if (currentTab === 0) {
      fetchProyectos();
      fetchEmpresas();
    } else if (currentTab === 1) {
      fetchDivisiones();
      fetchProyectos();
      fetchEmpresas();
    } else if (currentTab === 2) {
      fetchData();
      fetchEmpresas();
    }
  }, [currentTab, empresaIdsKey, empresaIds, fetchData, fetchProyectos, fetchDivisiones, fetchEmpresas]);

  // Opciones calculadas para filtros
  const catalogoEmpresas = useMemo(() => {
    const list = new Set();
    const source = currentTab === 0 ? proyectosData : currentTab === 1 ? divisionesData : data;
    source.forEach(item => {
      const emp = item.NOMBRE_EMPRESA || (empresasDisponibles.find(e => e.EMPRESAID === item.IDEMPRESA)?.NOMBRE_EMPRESA);
      if (emp) list.add(emp);
    });
    return Array.from(list).sort();
  }, [currentTab, proyectosData, divisionesData, data, empresasDisponibles]);

  const catalogoProyectos = useMemo(() => {
    const list = new Set();
    if (currentTab === 0) {
      proyectosData.forEach(p => p.NOMBRE_PROYECTO && list.add(p.NOMBRE_PROYECTO));
    } else if (currentTab === 1) {
      divisionesData.forEach(d => d.PROYECTO && list.add(d.PROYECTO));
    } else {
      data.forEach(c => c.PROYECTO && list.add(c.PROYECTO));
    }
    return Array.from(list).sort();
  }, [currentTab, proyectosData, divisionesData, data]);

  const catalogoGrupos = useMemo(() => {
    const list = new Set();
    if (currentTab === 1) {
      divisionesData.forEach(d => d.NOMBRE_DIVISION && list.add(d.NOMBRE_DIVISION));
    } else if (currentTab === 2) {
      data.forEach(c => (c.DIVISION || c.GRUPO) && list.add(c.DIVISION || c.GRUPO));
    }
    return Array.from(list).sort();
  }, [currentTab, divisionesData, data]);

  // Datos filtrados según pestaña activa
  const filteredData = useMemo(() => {
    let source = currentTab === 0 ? proyectosData : currentTab === 1 ? divisionesData : data;
    
    if (appliedEmpresas.length > 0) {
      source = source.filter(item => {
        const emp = item.NOMBRE_EMPRESA || (empresasDisponibles.find(e => e.EMPRESAID === item.IDEMPRESA)?.NOMBRE_EMPRESA);
        return appliedEmpresas.includes(emp);
      });
    }

    if (appliedProyectos.length > 0) {
      source = source.filter(item => {
        const proy = item.NOMBRE_PROYECTO || item.PROYECTO;
        return appliedProyectos.includes(proy);
      });
    }

    if (appliedGrupos.length > 0 && currentTab !== 0) {
      source = source.filter(item => {
        const grp = item.NOMBRE_DIVISION || item.DIVISION || item.GRUPO;
        return appliedGrupos.includes(grp);
      });
    }

    if (appliedBuscar.trim()) {
      const q = appliedBuscar.toLowerCase().trim();
      source = source.filter(item => {
        const str = Object.values(item).filter(v => typeof v === 'string' || typeof v === 'number').join(' ').toLowerCase();
        return str.includes(q);
      });
    }

    return source;
  }, [currentTab, proyectosData, divisionesData, data, appliedEmpresas, appliedProyectos, appliedGrupos, appliedBuscar, empresasDisponibles]);

  const abrirModalProy = (row = null) => {
    fetchEmpresas();
    setErrorsProy({});
    if (row) {
      setFormProy({
        ID_PROYECTO: row.ID_PROYECTO,
        NOMBRE_PROYECTO: row.NOMBRE_PROYECTO || '',
        IDEMPRESA: row.IDEMPRESA != null ? Number(row.IDEMPRESA) : (empresaIds.length === 1 ? empresaIds[0] : '')
      });
    } else {
      setFormProy({
        ID_PROYECTO: null,
        NOMBRE_PROYECTO: '',
        IDEMPRESA: empresaIds.length === 1 ? empresaIds[0] : ''
      });
    }
    setModalProyOpen(true);
  };

  const handleGuardarProyecto = async () => {
    const errs = {};
    if (!formProy.IDEMPRESA) errs.IDEMPRESA = 'Debe seleccionar una empresa';
    if (!formProy.NOMBRE_PROYECTO.trim()) errs.NOMBRE_PROYECTO = 'El nombre del proyecto es obligatorio';
    if (Object.keys(errs).length > 0) {
      setErrorsProy(errs);
      return;
    }
    try {
      await axios.post(`${apiUrl}/crearEditarProyecto`, {
        ID_PROYECTO: formProy.ID_PROYECTO,
        NOMBRE_PROYECTO: formProy.NOMBRE_PROYECTO.trim(),
        IDEMPRESA: formProy.IDEMPRESA,
        SESSION: loggedInUsername
      });
      handleOpenSweetAlert('Proyecto guardado correctamente', 'success');
      setModalProyOpen(false);
      fetchProyectos();
      fetchDivisiones();
      fetchData();
    } catch (e) {
      handleOpenSweetAlert('Error al guardar proyecto', 'error');
    }
  };

  const cambiarEstadoProyecto = async (row) => {
    try {
      await axios.post(`${apiUrl}/desactivarProyecto`, {
        ID_PROYECTO: row.ID_PROYECTO,
        ACTIVO: row.ACTIVO ? 0 : 1
      });
      handleOpenSweetAlert(row.ACTIVO ? 'Proyecto desactivado' : 'Proyecto activado', 'success');
      fetchProyectos();
      fetchDivisiones();
      fetchData();
    } catch (e) {
      handleOpenSweetAlert('Error al cambiar estado', 'error');
    }
  };

  const abrirModalDiv = (row = null) => {
    fetchEmpresas();
    setErrorsDiv({});
    if (row) {
      setFormDiv({
        ID_DIVISION: row.ID_DIVISION,
        NOMBRE_DIVISION: row.NOMBRE_DIVISION || '',
        ID_PROYECTO: row.ID_PROYECTO || '',
        IDEMPRESA: row.IDEMPRESA || ''
      });
    } else {
      setFormDiv({
        ID_DIVISION: null,
        NOMBRE_DIVISION: '',
        ID_PROYECTO: '',
        IDEMPRESA: empresaIds.length === 1 ? empresaIds[0] : ''
      });
    }
    setModalDivOpen(true);
  };

  const abrirModalAsignar = async (row) => {
    setSelectedDivisionForAssign(row);
    setSearchCcText('');
    setSelectedCcids([]);
    setModalAsignarOpen(true);

    try {
      const res = await axios.post(`${apiUrl}/centrosCostoPorDivision`, {
        ID_DIVISION: row.ID_DIVISION,
        NOMBRE_DIVISION: row.NOMBRE_DIVISION
      });
      setSelectedCcids(res.data?.ccids || []);
    } catch (e) {
      console.error('Error cargando centros de costo asignados:', e);
    }
  };

  const handleGuardarAsignacionMasiva = async () => {
    if (!selectedDivisionForAssign) return;
    if (selectedCcids.length === 0) {
      handleOpenSweetAlert('Selecciona al menos un centro de costo para asignar', 'warning');
      return;
    }

    setLoadingAssign(true);
    try {
      const res = await axios.post(`${apiUrl}/asignarCentrosCostoMasivo`, {
        ID_DIVISION: selectedDivisionForAssign.ID_DIVISION,
        NOMBRE_DIVISION: selectedDivisionForAssign.NOMBRE_DIVISION,
        ID_PROYECTO: selectedDivisionForAssign.ID_PROYECTO || null,
        NOMBRE_PROYECTO: selectedDivisionForAssign.PROYECTO || null,
        ccids: selectedCcids,
        SESSION: loggedInUsername
      });

      handleOpenSweetAlert(res.data?.message || 'Centros de costo asignados correctamente', 'success');
      setModalAsignarOpen(false);
      fetchData();
      fetchDivisiones();
    } catch (e) {
      handleOpenSweetAlert(e.response?.data?.error || 'Error al asignar centros de costo', 'error');
    } finally {
      setLoadingAssign(false);
    }
  };

  const handleToggleCcid = (ccid) => {
    setSelectedCcids(prev => 
      prev.includes(ccid) ? prev.filter(id => id !== ccid) : [...prev, ccid]
    );
  };

  const handleSelectAllFiltered = (filteredItems) => {
    const filteredIds = filteredItems.map(item => item.ID_UNICO);
    const allSelected = filteredIds.every(id => selectedCcids.includes(id));

    if (allSelected) {
      setSelectedCcids(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedCcids(prev => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const handleGuardarDivision = async () => {
    const errs = {};
    if (!formDiv.IDEMPRESA) errs.IDEMPRESA = 'Debe seleccionar una empresa';
    if (!formDiv.NOMBRE_DIVISION.trim()) errs.NOMBRE_DIVISION = 'El nombre del grupo es obligatorio';
    if (!formDiv.ID_PROYECTO) errs.ID_PROYECTO = 'Debe asociar el grupo a un proyecto';
    if (Object.keys(errs).length > 0) {
      setErrorsDiv(errs);
      return;
    }
    try {
      await axios.post(`${apiUrl}/crearEditarDivision`, {
        ID_DIVISION: formDiv.ID_DIVISION,
        NOMBRE_DIVISION: formDiv.NOMBRE_DIVISION.trim(),
        ID_PROYECTO: formDiv.ID_PROYECTO,
        IDEMPRESA: formDiv.IDEMPRESA,
        SESSION: loggedInUsername
      });
      handleOpenSweetAlert('Grupo guardado correctamente', 'success');
      setModalDivOpen(false);
      fetchDivisiones();
      fetchData();
    } catch (e) {
      handleOpenSweetAlert('Error al guardar grupo', 'error');
    }
  };

  const cambiarEstadoDivision = async (row) => {
    try {
      await axios.post(`${apiUrl}/desactivarDivision`, {
        ID_DIVISION: row.ID_DIVISION,
        ACTIVO: row.ACTIVO ? 0 : 1
      });
      handleOpenSweetAlert(row.ACTIVO ? 'División desactivada' : 'División activada', 'success');
      fetchDivisiones();
      fetchData();
    } catch (e) {
      handleOpenSweetAlert('Error al cambiar estado', 'error');
    }
  };

  // ───────────────────────────────────────────────────────────────────────
  // CONFIRMACIONES Y BOTONES DE TABLAS
  // ───────────────────────────────────────────────────────────────────────
  const [confirmModal, setConfirmModal] = useState({ open: false, message: '', action: '', row: null });

  const cambiarEstadoCentroCosto = useCallback(async (row) => {
    const nuevoEstado = row.ACTIVO ? 0 : 1;
    try {
      await axios.post(`${apiUrl}/desactivarCentroCosto`, {
        CCID: row.ID_UNICO,
        ACTIVO: nuevoEstado,
        SESSION: loggedInUsername
      });
      handleOpenSweetAlert(nuevoEstado ? 'Centro de costo activado' : 'Centro de costo desactivado', 'success');
      fetchData();
    } catch (err) {
      handleOpenSweetAlert('Error al cambiar estado del centro de costo', 'error');
    }
  }, [apiUrl, loggedInUsername, fetchData]);

  const openConfirmation = useCallback((action, row, message) => {
    setConfirmModal({ open: true, action, row, message });
  }, []);

  const handleConfirmAction = () => {
    if (confirmModal.action === 'cambiarEstado') {
      cambiarEstadoCentroCosto(confirmModal.row);
    } else if (confirmModal.action === 'cambiarEstadoProy') {
      cambiarEstadoProyecto(confirmModal.row);
    } else if (confirmModal.action === 'cambiarEstadoDiv') {
      cambiarEstadoDivision(confirmModal.row);
    }
    setConfirmModal({ open: false, action: '', row: null, message: '' });
  };

  const handleCancelAction = () => {
    setConfirmModal({ open: false, action: '', row: null, message: '' });
  };

  const buttonsCC = useMemo(() => [
    {
      id: 1,
      label: 'Editar',
      onClick: abrirModal,
      icon: <EditIcon />,
      buttonColor: '#2196f3',
      buttonFontColor: '#ffffff',
      isVisible: true,
      disabled: (row) => !row.ACTIVO,
    },
    {
      id: 2,
      label: (row) => row.ACTIVO ? 'Desactivar' : 'Activar',
      onClick: (row) => openConfirmation('cambiarEstado', row, row.ACTIVO ? '¿Desactivar centro de costo?' : '¿Activar centro de costo?'),
      icon: (row) => row.ACTIVO ? <BlockIcon /> : <CheckCircleIcon />,
      buttonColor: (row) => row.ACTIVO ? '#e53935' : '#43a047',
      buttonFontColor: '#ffffff',
      isVisible: true,
    },
  ], [abrirModal, openConfirmation]);

  const buttonsProy = useMemo(() => [
    {
      id: 1,
      label: 'Editar',
      onClick: (row) => abrirModalProy(row),
      icon: <EditIcon />,
      buttonColor: '#2196f3',
      buttonFontColor: '#ffffff',
      isVisible: true,
      disabled: (row) => !row.ACTIVO,
    },
    {
      id: 2,
      label: (row) => row.ACTIVO ? 'Desactivar' : 'Activar',
      onClick: (row) => openConfirmation('cambiarEstadoProy', row, row.ACTIVO ? '¿Desactivar proyecto?' : '¿Activar proyecto?'),
      icon: (row) => row.ACTIVO ? <BlockIcon /> : <CheckCircleIcon />,
      buttonColor: (row) => row.ACTIVO ? '#e53935' : '#43a047',
      buttonFontColor: '#ffffff',
      isVisible: true,
    },
  ], [openConfirmation]);

  const buttonsDiv = useMemo(() => [
    {
      id: 1,
      label: 'Asignar CCs',
      onClick: (row) => abrirModalAsignar(row),
      icon: <PlaylistAddCheckIcon />,
      buttonColor: '#2e7d32',
      buttonFontColor: '#ffffff',
      isVisible: true,
      disabled: (row) => !row.ACTIVO,
    },
    {
      id: 2,
      label: 'Editar',
      onClick: (row) => abrirModalDiv(row),
      icon: <EditIcon />,
      buttonColor: '#2196f3',
      buttonFontColor: '#ffffff',
      isVisible: true,
      disabled: (row) => !row.ACTIVO,
    },
    {
      id: 3,
      label: (row) => row.ACTIVO ? 'Desactivar' : 'Activar',
      onClick: (row) => openConfirmation('cambiarEstadoDiv', row, row.ACTIVO ? '¿Desactivar división?' : '¿Activar división?'),
      icon: (row) => row.ACTIVO ? <BlockIcon /> : <CheckCircleIcon />,
      buttonColor: (row) => row.ACTIVO ? '#e53935' : '#43a047',
      buttonFontColor: '#ffffff',
      isVisible: true,
    },
  ], [openConfirmation]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', p: 'clamp(6px, 0.8vw, 12px)', gap: 'clamp(6px, 0.8vw, 10px)', bgcolor: '#f8fafc' }}>
      {/* TARJETA UNIFICADA: PESTAÑAS + FILTROS */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          p: 'clamp(8px, 1vw, 12px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'clamp(6px, 0.8vw, 10px)',
          flexShrink: 0
        }}
      >
        {/* FILA SUPERIOR: TABS DE NAVEGACIÓN + BOTÓN AGREGAR */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', pb: 'clamp(3px, 0.4vw, 5px)' }}>
          <Tabs
            value={currentTab}
            onChange={(e, val) => setCurrentTab(val)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 'clamp(28px, 2.2vw, 34px)',
              '& .MuiTab-root': {
                minHeight: 'clamp(28px, 2.2vw, 34px)',
                py: 0,
                px: 'clamp(8px, 1vw, 14px)',
                fontSize: 'clamp(0.68rem, 0.65rem + 0.08vw, 0.78rem)',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '7px',
                '&.Mui-selected': {
                  color: '#0655A5',
                }
              }
            }}
          >
            <Tab icon={<AccountTreeIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 17px) !important' }} />} iconPosition="start" label="Proyectos" />
            <Tab icon={<FolderIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 17px) !important' }} />} iconPosition="start" label="Grupos" />
            <Tab icon={<ListAltIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 17px) !important' }} />} iconPosition="start" label="Centros de Costo" />
          </Tabs>

          {/* Botón Agregar */}
          <BotonAgregar
            title={
              currentTab === 0
                ? 'Agregar Proyecto'
                : currentTab === 1
                ? 'Agregar Grupo'
                : 'Agregar Centro de Costo'
            }
            onClick={() => {
              if (currentTab === 0) abrirModalProy(null);
              else if (currentTab === 1) abrirModalDiv(null);
              else abrirModal(null);
            }}
          />
        </Box>

        {/* FILA INFERIOR: BARRA DE FILTROS FLUIDA */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(6px, 0.8vw, 10px)',
            flexWrap: 'wrap',
            pt: 0.2
          }}
        >
          {/* 1. Empresa */}
          <Autocomplete
            multiple
            forcePopupIcon
            size="small"
            options={catalogoEmpresas}
            value={filtroEmpresas}
            onChange={(_, val) => setFiltroEmpresas(val)}
            renderTags={() => null}
            renderInput={(params) => renderSummaryInput(params, 'Empresa', filtroEmpresas)}
            sx={filterFieldSx}
            limitTags={1}
          />

          {/* 2. Proyecto */}
          <Autocomplete
            multiple
            forcePopupIcon
            size="small"
            options={catalogoProyectos}
            value={filtroProyectos}
            onChange={(_, val) => setFiltroProyectos(val)}
            renderTags={() => null}
            renderInput={(params) => renderSummaryInput(params, 'Proyecto', filtroProyectos)}
            sx={filterFieldSx}
            limitTags={1}
          />

          {/* 3. Grupo (solo en pestañas 1 y 2) */}
          {currentTab !== 0 && (
            <Autocomplete
              multiple
              forcePopupIcon
              size="small"
              options={catalogoGrupos}
              value={filtroGrupos}
              onChange={(_, val) => setFiltroGrupos(val)}
              renderTags={() => null}
              renderInput={(params) => renderSummaryInput(params, 'Grupo', filtroGrupos)}
              sx={filterFieldSx}
              limitTags={1}
            />
          )}

          {/* 4. Búsqueda rápida general */}
          <TextField
            size="small"
            placeholder="Buscar en la tabla..."
            value={filtroBuscar}
            onChange={(e) => setFiltroBuscar(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleBuscar();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 'clamp(14px, 1.1vw, 17px)', color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              ...filterFieldSx,
              width: 'clamp(160px, 16vw, 230px)',
              '& .MuiOutlinedInput-root': {
                ...filterFieldSx['& .MuiOutlinedInput-root'],
                height: 'clamp(28px, 2.2vw, 32px)',
              }
            }}
          />

          {/* Botones Buscar y Limpiar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, ml: 'auto' }}>
            <Button
              variant="contained"
              onClick={handleBuscar}
              startIcon={<SearchIcon sx={{ fontSize: 'clamp(13px, 1vw, 16px) !important' }} />}
              sx={{
                height: 'clamp(28px, 2.2vw, 32px)',
                px: 'clamp(12px, 1.2vw, 18px)',
                borderRadius: '8px',
                fontSize: 'clamp(0.66rem, 0.62rem + 0.08vw, 0.74rem)',
                fontWeight: 700,
                textTransform: 'none',
                backgroundColor: '#0655A5',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#054785',
                  boxShadow: 'none'
                }
              }}
            >
              Buscar
            </Button>

            <Button
              variant="outlined"
              onClick={handleLimpiar}
              startIcon={<CloseIcon sx={{ fontSize: 'clamp(13px, 1vw, 16px) !important' }} />}
              sx={{
                height: 'clamp(28px, 2.2vw, 32px)',
                px: 'clamp(10px, 1vw, 14px)',
                borderRadius: '8px',
                fontSize: 'clamp(0.66rem, 0.62rem + 0.08vw, 0.74rem)',
                fontWeight: 700,
                textTransform: 'none',
                borderColor: '#fca5a5',
                color: '#dc2626',
                bgcolor: 'rgba(239, 68, 68, 0.04)',
                '&:hover': {
                  borderColor: '#ef4444',
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                }
              }}
            >
              Limpiar
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* CONTENIDO TAB 0: PROYECTOS */}
      {currentTab === 0 && (
        <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <CollapsibleTable
            buttons={buttonsProy}
            dataTable={filteredData}
            showExportButton={true}
            density="small"
            compactActions
            maxInlineActions={2}
            hiddenFields={['IDEMPRESA', 'ID_PROYECTO']}
            columnLabels={{
              NOMBRE_PROYECTO: 'Nombre Proyecto',
              NOMBRE_EMPRESA: 'Empresa',
            }}
            rightButtons={
              <ExportButton
                data={filteredData}
                fileName="Proyectos_CC.xlsx"
                hiddenFields={['IDEMPRESA', 'ID_PROYECTO']}
              />
            }
          />
        </Box>
      )}

      {/* CONTENIDO TAB 1: DIVISIONES / GRUPOS */}
      {currentTab === 1 && (
        <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <CollapsibleTable
            buttons={buttonsDiv}
            dataTable={filteredData}
            showExportButton={true}
            density="small"
            compactActions
            maxInlineActions={3}
            hiddenFields={['IDEMPRESA', 'ID_DIVISION', 'ID_PROYECTO']}
            columnLabels={{
              NOMBRE_DIVISION: 'Grupo',
              PROYECTO: 'Proyecto Asociado',
              NOMBRE_EMPRESA: 'Empresa',
            }}
            rightButtons={
              <ExportButton
                data={filteredData}
                fileName="Divisiones_CC.xlsx"
                hiddenFields={['IDEMPRESA', 'ID_DIVISION', 'ID_PROYECTO']}
              />
            }
          />
        </Box>
      )}

      {/* CONTENIDO TAB 2: CENTROS DE COSTO */}
      {currentTab === 2 && (
        <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <CollapsibleTable
            buttons={buttonsCC}
            dataTable={filteredData}
            showExportButton={true}
            density="small"
            compactActions
            maxInlineActions={3}
            hiddenFields={[
              'IDEMPRESA',
              'ID_UNICO',
              'NUMERO_CC',
              'CENTRO_COSTO',
              'ID_PROYECTO',
              'NOMBRE_PROYECTO',
              'ID_DIVISION',
              'GRUPO_CC',
              'GRUPO'
            ]}
            columnLabels={{
              CCID: 'CCID',
              NOMBRE: 'Nombre Centro Costo',
              NUMERO: 'Número',
              NOMBRE_EMPRESA: 'Empresa',
              PROYECTO: 'Proyecto',
              DIVISION: 'Grupo',
              ACTIVO: 'Activo',
            }}
            rightButtons={
              <ExportButton
                data={filteredData}
                fileName="CentrosCosto.xlsx"
                hiddenFields={[
                  'IDEMPRESA',
                  'ID_UNICO',
                  'NUMERO_CC',
                  'CENTRO_COSTO',
                  'ID_PROYECTO',
                  'NOMBRE_PROYECTO',
                  'ID_DIVISION',
                  'GRUPO_CC',
                  'GRUPO'
                ]}
              />
            }
            persistEnabled
            persistKey="CentroCostos"
            persistVista="layout"
            usuarioId={usuarioId}
            empresaId={empresaIdPref}
            apiUrl={apiUrl}
          />
        </Box>
      )}

      {/* MODAL CENTRO DE COSTO */}
      <ModalCentroCosto
        open={modalOpen}
        onClose={cerrarModal}
        onSave={fetchData}
        centroCostoId={selectedCC?.ID_UNICO}
        formData={formData}
        handleInputChange={handleInputChange}
        usuario={loggedInUsername}
        empresaId={empresaIds}
      />

      {/* MODAL PROYECTO */}
      <Dialog
        open={modalProyOpen}
        disableRestoreFocus
        onClose={() => setModalProyOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '14px',
            overflow: 'hidden',
            boxShadow: '0 20px 48px rgba(0,0,0,0.18)',
            border: '1px solid #e2e8f0',
            maxWidth: 'clamp(330px, 32vw, 480px)'
          }
        }}
      >
        <Box
          sx={{
            px: 'clamp(14px, 1.6vw, 22px)',
            py: 'clamp(10px, 1.2vw, 15px)',
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.2
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 900,
                fontSize: 'clamp(0.88rem, 0.82rem + 0.35vw, 1.12rem)',
                lineHeight: 1.2,
                letterSpacing: '-0.01em'
              }}
              noWrap
            >
              {formProy.ID_PROYECTO ? 'Editar Proyecto' : 'Nuevo Proyecto'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                opacity: 0.88,
                fontSize: 'clamp(0.64rem, 0.60rem + 0.15vw, 0.78rem)',
                mt: 0.2,
                display: 'block'
              }}
            >
              Nivel 1 de la estructura de centros de costo
            </Typography>
          </Box>

          <IconButton
            onClick={() => setModalProyOpen(false)}
            size="small"
            sx={{
              color: 'white',
              p: 'clamp(3px, 0.4vw, 6px)',
              bgcolor: 'rgba(255,255,255,0.12)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
            }}
          >
            <CloseIcon sx={{ fontSize: 'clamp(15px, 1.2vw, 20px)' }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 'clamp(14px, 1.6vw, 22px)', bgcolor: '#ffffff' }}>
          <Stack spacing={'clamp(10px, 1.2vw, 16px)'}>
            <FormControl fullWidth size="small" error={Boolean(errorsProy.IDEMPRESA)} sx={elegantInputSx}>
              <InputLabel>Empresa Asociada *</InputLabel>
              <Select
                value={formProy.IDEMPRESA || ''}
                label="Empresa Asociada *"
                onChange={(e) => {
                  setFormProy({ ...formProy, IDEMPRESA: e.target.value });
                  if (errorsProy.IDEMPRESA) setErrorsProy(prev => ({ ...prev, IDEMPRESA: '' }));
                }}
                MenuProps={{ disableRestoreFocus: true }}
              >
                <MenuItem value=""><em>-- Seleccionar Empresa --</em></MenuItem>
                {empresasDisponibles.map((emp) => (
                  <MenuItem key={emp.EMPRESAID} value={emp.EMPRESAID}>
                    {emp.NOMBRE_EMPRESA}
                  </MenuItem>
                ))}
              </Select>
              {errorsProy.IDEMPRESA && (
                <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.3, fontWeight: 600, fontSize: 'clamp(0.64rem, 0.60rem + 0.1vw, 0.72rem)' }}>
                  {errorsProy.IDEMPRESA}
                </Typography>
              )}
            </FormControl>

            <TextField
              autoFocus
              size="small"
              label="Nombre del Proyecto *"
              fullWidth
              value={formProy.NOMBRE_PROYECTO}
              onChange={(e) => {
                setFormProy({ ...formProy, NOMBRE_PROYECTO: e.target.value });
                if (errorsProy.NOMBRE_PROYECTO) setErrorsProy(prev => ({ ...prev, NOMBRE_PROYECTO: '' }));
              }}
              error={Boolean(errorsProy.NOMBRE_PROYECTO)}
              helperText={errorsProy.NOMBRE_PROYECTO}
              placeholder="Ej: PLATAFORMAS, MARINO..."
              sx={elegantInputSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 'clamp(14px, 1.6vw, 22px)',
            py: 'clamp(8px, 0.9vw, 12px)',
            bgcolor: '#ffffff',
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.2
          }}
        >
          <Button
            onClick={() => setModalProyOpen(false)}
            sx={{
              borderRadius: '8px',
              px: 'clamp(12px, 1.2vw, 18px)',
              py: 'clamp(4px, 0.4vw, 6px)',
              fontWeight: 700,
              fontSize: 'clamp(0.70rem, 0.66rem + 0.15vw, 0.82rem)',
              textTransform: 'none',
              color: '#64748b'
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleGuardarProyecto}
            sx={{
              borderRadius: '8px',
              px: 'clamp(14px, 1.5vw, 22px)',
              py: 'clamp(5px, 0.5vw, 7px)',
              fontWeight: 800,
              fontSize: 'clamp(0.70rem, 0.66rem + 0.15vw, 0.84rem)',
              textTransform: 'none',
              background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
              boxShadow: '0 3px 12px rgba(6, 85, 165, 0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #054785 0%, #0655A5 100%)',
                boxShadow: '0 5px 16px rgba(6, 85, 165, 0.4)'
              }
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DIVISION */}
      <Dialog
        open={modalDivOpen}
        disableRestoreFocus
        onClose={() => setModalDivOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '14px',
            overflow: 'hidden',
            boxShadow: '0 20px 48px rgba(0,0,0,0.18)',
            border: '1px solid #e2e8f0',
            maxWidth: 'clamp(330px, 32vw, 480px)'
          }
        }}
      >
        <Box
          sx={{
            px: 'clamp(14px, 1.6vw, 22px)',
            py: 'clamp(10px, 1.2vw, 15px)',
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.2
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 900,
                fontSize: 'clamp(0.88rem, 0.82rem + 0.35vw, 1.12rem)',
                lineHeight: 1.2,
                letterSpacing: '-0.01em'
              }}
              noWrap
            >
              {formDiv.ID_DIVISION ? 'Editar Grupo' : 'Nuevo Grupo'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                opacity: 0.88,
                fontSize: 'clamp(0.64rem, 0.60rem + 0.15vw, 0.78rem)',
                mt: 0.2,
                display: 'block'
              }}
            >
              Nivel 2 de la estructura de centros de costo
            </Typography>
          </Box>

          <IconButton
            onClick={() => setModalDivOpen(false)}
            size="small"
            sx={{
              color: 'white',
              p: 'clamp(3px, 0.4vw, 6px)',
              bgcolor: 'rgba(255,255,255,0.12)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
            }}
          >
            <CloseIcon sx={{ fontSize: 'clamp(15px, 1.2vw, 20px)' }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 'clamp(14px, 1.6vw, 22px)', bgcolor: '#ffffff' }}>
          <Stack spacing={'clamp(10px, 1.2vw, 16px)'}>
            <FormControl fullWidth size="small" error={Boolean(errorsDiv.IDEMPRESA)} sx={elegantInputSx}>
              <InputLabel>Empresa Asociada *</InputLabel>
              <Select
                value={formDiv.IDEMPRESA || ''}
                label="Empresa Asociada *"
                onChange={(e) => {
                  setFormDiv({ ...formDiv, IDEMPRESA: e.target.value });
                  if (errorsDiv.IDEMPRESA) setErrorsDiv(prev => ({ ...prev, IDEMPRESA: '' }));
                }}
                MenuProps={{ disableRestoreFocus: true }}
              >
                <MenuItem value=""><em>-- Seleccionar Empresa --</em></MenuItem>
                {empresasDisponibles.map((emp) => (
                  <MenuItem key={emp.EMPRESAID} value={emp.EMPRESAID}>
                    {emp.NOMBRE_EMPRESA}
                  </MenuItem>
                ))}
              </Select>
              {errorsDiv.IDEMPRESA && (
                <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.3, fontWeight: 600, fontSize: 'clamp(0.64rem, 0.60rem + 0.1vw, 0.72rem)' }}>
                  {errorsDiv.IDEMPRESA}
                </Typography>
              )}
            </FormControl>

            <TextField
              autoFocus
              size="small"
              label="Nombre del Grupo *"
              fullWidth
              value={formDiv.NOMBRE_DIVISION}
              onChange={(e) => {
                setFormDiv({ ...formDiv, NOMBRE_DIVISION: e.target.value });
                if (errorsDiv.NOMBRE_DIVISION) setErrorsDiv(prev => ({ ...prev, NOMBRE_DIVISION: '' }));
              }}
              error={Boolean(errorsDiv.NOMBRE_DIVISION)}
              helperText={errorsDiv.NOMBRE_DIVISION}
              placeholder="Ej: DUCTOS, INDIRECTOS, GENÉRICAS..."
              sx={elegantInputSx}
            />
            <FormControl fullWidth size="small" error={Boolean(errorsDiv.ID_PROYECTO)} sx={elegantInputSx}>
              <InputLabel>Proyecto Asociado *</InputLabel>
              <Select
                value={formDiv.ID_PROYECTO || ''}
                label="Proyecto Asociado *"
                onChange={(e) => {
                  setFormDiv({ ...formDiv, ID_PROYECTO: e.target.value });
                  if (errorsDiv.ID_PROYECTO) setErrorsDiv(prev => ({ ...prev, ID_PROYECTO: '' }));
                }}
                MenuProps={{ disableRestoreFocus: true }}
              >
                <MenuItem value=""><em>-- Seleccionar Proyecto --</em></MenuItem>
                {proyectosData.filter(p => p.ACTIVO).map((p) => (
                  <MenuItem key={p.ID_PROYECTO} value={p.ID_PROYECTO}>
                    {p.NOMBRE_PROYECTO} {p.NOMBRE_EMPRESA ? `(${p.NOMBRE_EMPRESA})` : ''}
                  </MenuItem>
                ))}
              </Select>
              {errorsDiv.ID_PROYECTO && (
                <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.3, fontWeight: 600, fontSize: 'clamp(0.64rem, 0.60rem + 0.1vw, 0.72rem)' }}>
                  {errorsDiv.ID_PROYECTO}
                </Typography>
              )}
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 'clamp(14px, 1.6vw, 22px)',
            py: 'clamp(8px, 0.9vw, 12px)',
            bgcolor: '#ffffff',
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.2
          }}
        >
          <Button
            onClick={() => setModalDivOpen(false)}
            sx={{
              borderRadius: '8px',
              px: 'clamp(12px, 1.2vw, 18px)',
              py: 'clamp(4px, 0.4vw, 6px)',
              fontWeight: 700,
              fontSize: 'clamp(0.70rem, 0.66rem + 0.15vw, 0.82rem)',
              textTransform: 'none',
              color: '#64748b'
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleGuardarDivision}
            sx={{
              borderRadius: '8px',
              px: 'clamp(14px, 1.5vw, 22px)',
              py: 'clamp(5px, 0.5vw, 7px)',
              fontWeight: 800,
              fontSize: 'clamp(0.70rem, 0.66rem + 0.15vw, 0.84rem)',
              textTransform: 'none',
              background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
              boxShadow: '0 3px 12px rgba(6, 85, 165, 0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #054785 0%, #0655A5 100%)',
                boxShadow: '0 5px 16px rgba(6, 85, 165, 0.4)'
              }
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL ASIGNACIÓN MASIVA DE CENTROS DE COSTO */}
      <Dialog
        open={modalAsignarOpen}
        disableRestoreFocus
        onClose={() => setModalAsignarOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
            border: '1px solid #e2e8f0',
            maxHeight: '88vh'
          }
        }}
      >
        {/* Header Premium con Gradiente Oficial del Sistema */}
        <Box
          sx={{
            px: 'clamp(16px, 2vw, 24px)',
            py: 'clamp(12px, 1.5vw, 16px)',
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                fontSize: 'clamp(0.95rem, 0.9rem + 0.25vw, 1.15rem)',
                lineHeight: 1.2,
                letterSpacing: '-0.01em'
              }}
              noWrap
            >
              Asignar Centros de Costo a División
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.6, alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                size="small"
                label={`División: ${selectedDivisionForAssign?.NOMBRE_DIVISION || ''}`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: 'clamp(0.68rem, 0.65rem + 0.08vw, 0.76rem)',
                  height: 22
                }}
              />
              {selectedDivisionForAssign?.PROYECTO && (
                <Chip
                  size="small"
                  label={`Proyecto: ${selectedDivisionForAssign.PROYECTO}`}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: 'clamp(0.68rem, 0.65rem + 0.08vw, 0.76rem)',
                    height: 22
                  }}
                />
              )}
              <Chip
                size="small"
                label={`${selectedCcids.length} seleccionados`}
                sx={{
                  bgcolor: '#ffffff',
                  color: '#0655A5',
                  fontWeight: 800,
                  fontSize: 'clamp(0.68rem, 0.65rem + 0.08vw, 0.76rem)',
                  height: 22
                }}
              />
            </Stack>
          </Box>

          <IconButton
            onClick={() => setModalAsignarOpen(false)}
            size="small"
            sx={{
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 'clamp(14px, 1.8vw, 20px)', bgcolor: '#f8fafc' }}>
          {/* BUSCADOR Y CONTROLES */}
          {(() => {
            const term = searchCcText.toLowerCase().trim();
            let filtered = data.filter(cc => {
              if (!term) return true;
              const nom = (cc.NOMBRE || cc.CENTRO_COSTO || '').toLowerCase();
              const num = String(cc.NUMERO || cc.NUMERO_CC || '').toLowerCase();
              const emp = String(cc.EMPRESA || '').toLowerCase();
              return nom.includes(term) || num.includes(term) || emp.includes(term);
            });

            // Ordenar por Número de Centro de Costo de forma natural
            filtered = [...filtered].sort((a, b) => {
              const na = String(a.NUMERO || a.NUMERO_CC || '').toLowerCase();
              const nb = String(b.NUMERO || b.NUMERO_CC || '').toLowerCase();
              if (na < nb) return -1;
              if (na > nb) return 1;
              return (a.NOMBRE || a.CENTRO_COSTO || '').localeCompare(b.NOMBRE || b.CENTRO_COSTO || '');
            });

            const filteredIds = filtered.map(item => item.ID_UNICO);
            const allSelected = filteredIds.length > 0 && filteredIds.every(id => selectedCcids.includes(id));
            const someSelected = filteredIds.some(id => selectedCcids.includes(id)) && !allSelected;
            const filteredSelectedCount = filtered.reduce((acc, o) => acc + (selectedCcids.includes(o.ID_UNICO) ? 1 : 0), 0);

            return (
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Buscar por número de CC, nombre o empresa..."
                    value={searchCcText}
                    onChange={(e) => setSearchCcText(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: '1.15rem', color: '#64748b' }} />
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      ...elegantInputSx,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        bgcolor: '#ffffff',
                        fontSize: 'clamp(0.8rem, 0.78rem + 0.1vw, 0.88rem)'
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleSelectAllFiltered(filtered)}
                    sx={{
                      whiteSpace: 'nowrap',
                      minWidth: 'clamp(140px, 12vw, 175px)',
                      borderRadius: '9px',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: 'clamp(0.74rem, 0.72rem + 0.08vw, 0.82rem)',
                      py: 0.8,
                      borderColor: '#cbd5e1',
                      color: '#334155',
                      bgcolor: '#ffffff',
                      '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' }
                    }}
                  >
                    {allSelected ? 'Deseleccionar Visibles' : 'Seleccionar Visibles'}
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={() => handleSelectAllFiltered(filtered)}
                        disabled={filtered.length === 0}
                        sx={{ p: 0.5, mr: 0.5, color: '#94a3b8', '&.Mui-checked': { color: '#0655A5' } }}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontSize: 'clamp(0.78rem, 0.75rem + 0.08vw, 0.84rem)', fontWeight: 700, color: '#334155' }}>
                          {allSelected
                            ? 'Quitar selección (visibles)'
                            : someSelected
                              ? 'Completar selección (visibles)'
                              : 'Seleccionar visibles'}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${filteredSelectedCount}/${filtered.length}`}
                          sx={{
                            fontSize: '0.68rem',
                            height: 18,
                            bgcolor: '#e2e8f0',
                            color: '#334155',
                            fontWeight: 700
                          }}
                        />
                      </Stack>
                    }
                  />

                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: 'clamp(0.7rem, 0.68rem + 0.06vw, 0.76rem)', fontWeight: 600 }}>
                    {filtered.length} centros de costo encontrados
                  </Typography>
                </Box>

                <Paper
                  variant="outlined"
                  sx={{
                    maxHeight: 'clamp(280px, 45vh, 420px)',
                    overflowY: 'auto',
                    p: 1,
                    borderRadius: '12px',
                    bgcolor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
                  }}
                >
                  {filtered.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center', color: '#64748b' }}>
                      <Typography variant="body2" sx={{ fontSize: 'clamp(0.8rem, 0.78rem + 0.1vw, 0.88rem)' }}>
                        No se encontraron centros de costo{searchCcText ? ` para “${searchCcText}”` : ''}.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={0.75}>
                      {filtered.map((cc) => {
                        const isChecked = selectedCcids.includes(cc.ID_UNICO);
                        const tieneOtraDivision = cc.DIVISION && cc.DIVISION !== selectedDivisionForAssign?.NOMBRE_DIVISION;

                        return (
                          <Box
                            key={cc.ID_UNICO}
                            onClick={() => handleToggleCcid(cc.ID_UNICO)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 'clamp(8px, 1vw, 10px)',
                              px: 'clamp(10px, 1.2vw, 14px)',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              border: isChecked ? '1.5px solid rgba(6, 85, 165, 0.4)' : '1px solid #f1f5f9',
                              bgcolor: isChecked ? 'rgba(6, 85, 165, 0.06)' : '#ffffff',
                              transition: 'all 0.15s ease-in-out',
                              '&:hover': {
                                bgcolor: isChecked ? 'rgba(6, 85, 165, 0.12)' : '#f8fafc',
                                borderColor: isChecked ? '#0655A5' : '#cbd5e1',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                              <Checkbox
                                checked={isChecked}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleToggleCcid(cc.ID_UNICO);
                                }}
                                sx={{
                                  p: 0.25,
                                  color: '#cbd5e1',
                                  '&.Mui-checked': { color: '#0655A5' }
                                }}
                              />
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                {/* 1. NÚMERO DE CENTRO DE COSTO EN NEGRO Y DESTACADO */}
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 900,
                                    color: '#0f172a',
                                    fontSize: 'clamp(0.88rem, 0.85rem + 0.12vw, 0.96rem)',
                                    lineHeight: 1.25,
                                    letterSpacing: '-0.01em'
                                  }}
                                >
                                  {cc.NUMERO || cc.NUMERO_CC || 'S/N'}
                                </Typography>
                                {/* 2. NOMBRE Y EMPRESA ABAJO */}
                                <Typography
                                  variant="body2"
                                  noWrap
                                  sx={{
                                    color: '#475569',
                                    fontSize: 'clamp(0.78rem, 0.75rem + 0.08vw, 0.84rem)',
                                    mt: 0.2
                                  }}
                                >
                                  <span style={{ fontWeight: 600, color: isChecked ? '#0655A5' : '#334155' }}>
                                    {cc.NOMBRE || cc.CENTRO_COSTO}
                                  </span>
                                  {cc.EMPRESA && (
                                    <span style={{ color: '#94a3b8' }}> • {cc.EMPRESA}</span>
                                  )}
                                </Typography>
                              </Box>
                            </Box>

                            {tieneOtraDivision && (
                              <Chip
                                size="small"
                                label={`En: ${cc.DIVISION}`}
                                sx={{
                                  fontSize: '0.7rem',
                                  height: 22,
                                  bgcolor: '#f1f5f9',
                                  color: '#475569',
                                  fontWeight: 700,
                                  border: '1px solid #e2e8f0'
                                }}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Paper>
              </Stack>
            );
          })()}
        </DialogContent>

        {/* Footer Premium */}
        <DialogActions
          sx={{
            px: 'clamp(16px, 2vw, 24px)',
            py: 'clamp(10px, 1.2vw, 14px)',
            bgcolor: '#ffffff',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#475569',
              fontWeight: 700,
              fontSize: 'clamp(0.76rem, 0.74rem + 0.08vw, 0.82rem)'
            }}
          >
            <strong>{selectedCcids.length}</strong> centros de costo seleccionados
          </Typography>

          <Stack direction="row" spacing={1.5}>
            <Button
              onClick={() => setModalAsignarOpen(false)}
              disabled={loadingAssign}
              sx={{
                borderRadius: '8px',
                px: 'clamp(14px, 1.5vw, 18px)',
                fontWeight: 700,
                fontSize: 'clamp(0.78rem, 0.75rem + 0.08vw, 0.84rem)',
                textTransform: 'none',
                color: '#64748b'
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleGuardarAsignacionMasiva}
              disabled={loadingAssign}
              startIcon={<PlaylistAddCheckIcon sx={{ fontSize: 'clamp(16px, 1.2vw, 20px)' }} />}
              sx={{
                borderRadius: '10px',
                px: 'clamp(16px, 2vw, 24px)',
                py: 'clamp(6px, 0.8vw, 8px)',
                fontWeight: 800,
                fontSize: 'clamp(0.78rem, 0.75rem + 0.1vw, 0.86rem)',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
                boxShadow: '0 4px 14px rgba(6,85,165,0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #054785 0%, #0655A5 100%)',
                  boxShadow: '0 6px 20px rgba(6,85,165,0.45)'
                }
              }}
            >
              {loadingAssign ? 'Guardando...' : `Guardar Asignación (${selectedCcids.length})`}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* MODAL CONFIRMACION */}
      <DoubleConfirmModal
        open={confirmModal.open}
        message={confirmModal.message}
        handleConfirm={handleConfirmAction}
        handleCancel={handleCancelAction}
      />
    </Box>
  );
}